// Canonical API-error → user-facing copy resolver. The wire envelope and
// `DomainError` both carry a stable machine code (e.g. `UNAUTHENTICATED`,
// `identity/photo_invalid`); `DomainError.message` is set to that code, so
// rendering `err.message` leaks the raw code to users. Route every
// error-display site through here instead: it maps the code to the curated
// copy in `messages/en/errors.json` and falls back when the code is unknown.
//
// Pure (no React hook) so it works in components, mutation files, and
// non-render callbacks alike. The copy source is the same `Errors` namespace
// the IntlProvider serves, so there's a single place to localize later.

import errorCopy from '../../messages/en/errors.json';
import { parseApiErrorCode } from './api-error';

type ErrorCode = keyof typeof errorCopy;

const DEFAULT_FALLBACK: string = errorCopy.UNKNOWN;

function codeOf(err: unknown): string | null {
  const nested = parseApiErrorCode(err);
  if (nested !== null) return nested;
  if (err !== null && typeof err === 'object') {
    const code = (err as { code?: unknown }).code;
    if (typeof code === 'string') return code;
  }
  return null;
}

/**
 * Resolve a thrown API error (or `{ code }`/`{ error: { code } }` shape) to
 * curated user-facing copy. Unknown codes and code-less errors return
 * `fallback` (default: the generic `UNKNOWN` message).
 */
export function errorMessage(err: unknown, fallback: string = DEFAULT_FALLBACK): string {
  const code = codeOf(err);
  if (code !== null && code in errorCopy) {
    return errorCopy[code as ErrorCode];
  }
  return fallback;
}
